"use client"; 

import { useState } from 'react';

import Link from 'next/link';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

import logger from '../../../lib/logger'

import ComparisonBlockTitle from './common/comparisonBlockTitle'
import ComparisonTableName from './common/comparisonTableName'

import { classNames, appWebsiteLink } from '../../../lib/helpers'

const DESCR_MAX_INITIAL_WORDS = 20  // max words to show initially


export default function ComparisonDetails({ apps=[] }) {
  logger.info("Component.ComparisonDetails rendering.");

  const [appA, appB] = apps
  
  // Title and Description 
  const appTitle = (app) => {
    if (app?.title) return app?.title
    if (app?.primary_category?.name) return `${app?.name} is a tool in the ${app?.primary_category?.name} category `
  }
  
  const appDescription = (app) => app?.description  // We can add autogenerated text here later.  
  const descriptionWordsCount = (description) => description?.split(' ')?.length
  const appDescriptionInitial = (app) => appDescription(app)?.split(' ')?.slice(0, DESCR_MAX_INITIAL_WORDS)?.join(' ')

  const showDescriptionReadMore = (app) => {
    const descr = appDescription(app)
    const wordsCount = descriptionWordsCount(descr)
    return wordsCount > DESCR_MAX_INITIAL_WORDS
  }
 

  const [showMoreButtonsClicked, setShowMoreButtonsClicked] = useState([false, false])  // for both buttons 


  // Categories - making them order by primary and then if present on both apps and then by name
  const appCategoriesAllFlat = [
    ...appA?.app_categories?.map(x => ({ app: { id: appA?.id }, ...x })),
    ...appB?.app_categories?.map(x => ({ app: { id: appB?.id }, ...x }))
  ]  
  const categoriesAllFlat = apps?.map(app => app?.app_categories?.map(x => x?.category))?.flat()
  const categoriesListUnique = [...new Map(categoriesAllFlat.map((x) => [x["id"], x])).values()];
  const categories = categoriesListUnique?.map(cat => {
    const countApps = appCategoriesAllFlat?.filter(x => x?.category?.id === cat?.id)?.length 
    const countPrimary = appCategoriesAllFlat?.filter(x => x?.category?.id === cat?.id)?.filter(x => x?.is_primary)?.length 
    return {
      ...cat, 
      count_apps: countApps,
      count_primary: countPrimary
    }
  })

  const appCategoriesList = (app) => {
    return app?.app_categories?.map(x => categories?.find(y => {
      return y?.id === x?.category?.id
    }))?.sort((a, b) => {
      if (b.count_primary === a.count_primary) {
        if (b.count_apps === a.count_apps) {
          if(a.name < b.name) { return -1; }
          if(a.name > b.name) { return 1; }
          return 0;
        } else {
          return b.count_apps - a.count_apps
        }
      } else {
        return b.count_primary - a.count_primary
      }        
    })
  }

  const isCategoryPrimary = (category, app) => app?.app_categories?.find(x => x?.category?.id === category?.id)?.is_primary
  

  // Platforms 
  const platformsAll = apps?.map(app => app?.app_platforms?.map(x => x?.platform))?.flat()
  const platformsUniq = [...new Map(platformsAll.map((x) => [x["slug"], x])).values()]
  const platforms = platformsUniq?.map(platform => {
    const supported_a = appA?.app_platforms?.map(x => x?.platform?.slug)?.includes(platform?.slug)
    const supported_b = appB?.app_platforms?.map(x => x?.platform?.slug)?.includes(platform?.slug)
    return { ...platform, supported_a, supported_b }
  })?.sort((a,b) => (a.order - b.order));

  const isPlatformSupported = (platform, idx) => {
    const currentPlatform = platforms?.find(x => x?.slug === platform?.slug)
    if (idx === 0) return currentPlatform?.supported_a
    return currentPlatform?.supported_b
  }



  return <>
    <div className="mt-6 mb-12">

      <ComparisonBlockTitle name='Overview' href='overview' iconName="Bars4Icon" />

      <div className="px-4 pt-0 pb-1  mt-9 bg-white rounded border border-gray-200 ">
        {/* Title and Description */}
        <div className=" grid grid-cols-2 gap-x-4 ">
          {[appA, appB]?.map((app, idx) => (
            <div key={idx} className="">
              <ComparisonTableName 
                keyValue={`title-${idx}`} 
                title={`About ${app?.name}`}
              />

              <p className="text-sm sm:text-base text-gray-900">
                {appTitle(app)}
              </p>

              {!!appDescription(app) && (
                <p className="mt-2 text-sm sm:text-base text-gray-900">
                  {showMoreButtonsClicked[idx] ? appDescription(app) : appDescriptionInitial(app)}
                  
                  {showDescriptionReadMore(app) && (
                    <>                      
                      {!showMoreButtonsClicked[idx] ? <span>... </span> : `${` `}` }
                      <button 
                        className="text-crisp-600 "
                        onClick={() => {
                          const [btnA, btnB] = showMoreButtonsClicked; 
                          const btns = [ idx === 0 ? !btnA : btnA, idx === 1 ? !btnB : btnB ]
                          setShowMoreButtonsClicked(btns); 
                        }}
                      >
                        {showMoreButtonsClicked[idx] ? 'Read Less' : 'Read More' }
                      </button>                      
                    </>
                  )}

                </p>
              )}

            </div>     
          ))}
        </div>  


        {/* Website URL  */}
        <div className="mt-2 mb-3 grid grid-cols-2 gap-x-4 ">
          {[appA, appB]?.map((app, idx) => (
            <div key={idx} className="">

              {app?.domain && (
                <a href={appWebsiteLink(app)} target="_blank" 
                  className="text-crisp-600 hover:underline after:content-['_↗']"
                  // GlobeAltIcon //🌐
                >
                  {app?.domain}
                </a>
              )}

            </div>     
          ))}
        </div>  


        {/* Categories */}
        <div className="mt-2 mb-6 grid grid-cols-2 gap-x-4 ">
          {[appA, appB]?.map((app, idx) => (
            <div key={idx} className="">

              <ComparisonTableName 
                keyValue={`title-${idx}`} 
                title={`${app?.name} categories`}
              />

              <ul>
                {appCategoriesList(app)?.map((category, categoryIndex) => (
                  <li key={categoryIndex} className={classNames(isCategoryPrimary(category, app) ? 
                    'bg-crisp-100 text-crisp-800 border-crisp-200' : 
                    'bg-white text-gray-500 border-gray-200', 
                    'mt-2 sm:mt-3 truncate px-2 py-0.5 rounded  border   tracking-tight text-sm sm:text-base font-medium')} 
                  >
                    <Link href={`/${category?.slug}`} className="">

                      <span className="truncate">
                        {category?.name}
                      </span>

                    </Link>
                  </li>
                ))}
              </ul>
            </div>     
          ))}
        </div>  


        {/* Platforms */}
        {(appA?.app_platforms?.length + appB?.app_platforms?.length > 0) && (
          <div className="mt-2 mb-6 grid grid-cols-2 gap-x-4 ">
            {[appA, appB]?.map((app, idx) => (
              <div key={idx} className="">

                <ComparisonTableName 
                  keyValue={`title-${idx}`} 
                  title={`${app?.name} platforms`}
                />

                {platforms?.map((platform, index) => (
                  <div key={index} className="flex gap-x-1.5 sm:gap-x-3 ">
                    <div className="w-full h-full truncate" >
                      <p className={classNames(isPlatformSupported(platform, idx) ? 'text-gray-900' : 'text-gray-300', 'text-sm sm:text-base ')}>
                        {platform?.name}                      
                      </p>
                    </div>
                    
                    <div className="flex-shrink-0" >
                      <span className="inline-flex items-center justify-center   ">
                        <span className="text-base font-medium leading-none ">
                          {isPlatformSupported(platform, idx) ? (
                            <CheckIcon className="h-6 w-6 text-green-500" />
                          ) : (
                            <XMarkIcon className="h-6 w-6 text-red-300" />
                          )}
                        </span>
                      </span>
                    </div>

                  </div>
                ))}
              </div>     
            ))}
          </div>
        )}
      </div>

    </div> 
  </>;
}
